import type { Question } from '../types';

// CBR 2026 EXAM STRUCTURE - 27 TOPICS ACROSS 3 TIERS
export interface TopicDef {
  id: string;
  name: string;
  tier: 'tier1' | 'tier2' | 'tier3';
  description: string;
}

// BACKWARD COMPATIBILITY: Old interface definitions
export interface CategoryPool {
  id: string;
  name: string;
  totalQuestions: number;
  topicPools: TopicPool[];
}

export interface TopicPool {
  id: string;
  name: string;
  category: string;
  questions: Question[];
  totalQuestions: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface ArchivePool {
  id: string;
  name: string;
  questions: Question[];
  isArchived: boolean;
  rotationEnabled: boolean;
}

export interface PracticeTestConfig {
  questionsPerTest: number;
  unseenPriority: number;
  weakAreaWeight: number;
}

// CBR 2026 TIER 1: CRITICAL (8 Topics) - Direct exam impact
export const TIER1_TOPICS: TopicDef[] = [
  { id: 'roadMarkings', name: 'Road Markings', tier: 'tier1', description: 'Yellow lines, white markings, haaientanden, arrow markings' },
  { id: 'motorwayRules', name: 'Motorway Rules', tier: 'tier1', description: 'Speed, lane discipline, emergency stops, merging' },
  { id: 'trafficSignRecognition', name: 'Traffic Sign Recognition', tier: 'tier1', description: 'Mandatory, prohibitory, warning, informational signs' },
  { id: 'vehicleHandling', name: 'Vehicle Handling', tier: 'tier1', description: 'Brake/steering scenarios, wet roads, emergencies' },
  { id: 'tramInteractions', name: 'Tram Interactions', tier: 'tier1', description: 'Tram priority, boarding zones, track crossing' },
  { id: 'riskAwareness', name: 'Risk Awareness', tier: 'tier1', description: 'Hidden pedestrians, child hazards, cyclist blind spots' },
  { id: 'nightVisibility', name: 'Night Visibility', tier: 'tier1', description: 'Unlit cyclists, dark conditions, reduced headlight range' },
  { id: 'speedLimits', name: 'Speed Limits', tier: 'tier1', description: 'Motorway, urban, rural speeds, Zone 30, weather adaptation, enforcement' }
];

// CBR 2026 TIER 2: IMPORTANT (11 Topics) - Practical driving skills
export const TIER2_TOPICS: TopicDef[] = [
  { id: 'weatherConditions', name: 'Weather Conditions', tier: 'tier2', description: 'Rain, snow, ice, wind scenarios' },
  { id: 'incidentFirstAid', name: 'Incident First Aid', tier: 'tier2', description: 'Emergency response, accident procedures' },
  { id: 'pedestrianCrossings', name: 'Pedestrian Crossings', tier: 'tier2', description: 'Zebra crossing rules, pedestrian priority' },
  { id: 'vehicleCategories', name: 'Vehicle Categories', tier: 'tier2', description: 'Truck/bus/motorcycle specific rules' },
  { id: 'constructionZones', name: 'Construction Zones', tier: 'tier2', description: 'Work site safety, temporary signs' },
  { id: 'laneChanging', name: 'Lane Changing', tier: 'tier2', description: 'Safe lane changing, mirror checks, blind spots, markings' },
  { id: 'overtaking', name: 'Overtaking', tier: 'tier2', description: 'Safe overtaking, visibility, road markings, procedures' },
  { id: 'technologySafety', name: 'Technology Safety', tier: 'tier2', description: 'ADAS features, vehicle technology safety' },
  { id: 'fatigueRest', name: 'Fatigue & Rest', tier: 'tier2', description: 'Driver wellness, rest requirements' },
  { id: 'environmentalZones', name: 'Environmental Zones', tier: 'tier2', description: 'Low-emission zones, clean air zones' },
  { id: 'vehicleDocumentation', name: 'Vehicle Documentation', tier: 'tier2', description: 'Registration, insurance, inspection' }
];

// CBR 2026 TIER 3: ENHANCEMENT (8 Topics) - Overall knowledge
export const TIER3_TOPICS: TopicDef[] = [
  { id: 'priorityRules', name: 'Priority Rules', tier: 'tier3', description: 'Priority at intersections, yield scenarios, tram priority' },
  { id: 'mandatorySigns', name: 'Mandatory Signs', tier: 'tier3', description: 'Blue circular mandatory instruction signs' },
  { id: 'trafficLightsSignals', name: 'Traffic Lights & Signals', tier: 'tier3', description: 'Traffic light colors, pedestrian signals, level crossings' },
  { id: 'prohibitorySigns', name: 'Prohibitory Signs', tier: 'tier3', description: 'Prohibition and restriction signs' },
  { id: 'roundaboutRules', name: 'Roundabout Rules', tier: 'tier3', description: 'Roundabout entry, lane discipline, cyclist priority' },
  { id: 'emergencyProcedures', name: 'Emergency Procedures', tier: 'tier3', description: 'Accident/breakdown response, emergency numbers' },
  { id: 'alcoholDrugs', name: 'Alcohol & Drugs', tier: 'tier3', description: 'Impairment awareness and legal limits' },
  { id: 'specializedDriving', name: 'Specialized Driving', tier: 'tier3', description: 'Towing, carrying loads, special vehicle handling' }
];

// BONUS: ADAS (User Request - Keep as Separate Topic)
export const BONUS_TOPICS: TopicDef[] = [
  { id: 'adasSafety', name: 'ADAS', tier: 'tier1', description: 'Advanced Driver Assistance Systems' }
];

// ALL CBR 2026 TOPICS (26) + ADAS BONUS (1) = 27 Total
export const ALL_CBR2026_TOPICS = [...TIER1_TOPICS, ...TIER2_TOPICS, ...TIER3_TOPICS];
export const ALL_TOPICS = [...ALL_CBR2026_TOPICS, ...BONUS_TOPICS];

// Create maps for quick lookup
export const CBR2026_TOPICS_BY_ID = new Map(ALL_CBR2026_TOPICS.map(t => [t.id, t]));
export const ALL_TOPICS_BY_ID = new Map(ALL_TOPICS.map(t => [t.id, t]));

// PRACTICE TEST CONFIGURATION
export const PRACTICE_TEST_CONFIG: PracticeTestConfig = {
  questionsPerTest: 10,
  unseenPriority: 0.7,
  weakAreaWeight: 0.3
};

// CBR 2026 category groupings used by questionMapper/questionSelector (endless mode)
// INSIGHT: Sign & rule recognition topics
export const INSIGHT_TOPICS = [
  'trafficSignRecognition', 'mandatorySigns', 'prohibitorySigns',
  'trafficLightsSignals', 'roundaboutRules', 'priorityRules',
  'roadMarkings', 'pedestrianCrossings'
] as const;
// HAZARD: Perception & situational awareness topics
export const HAZARD_TOPICS = [
  'hazardPerception', 'riskAwareness', 'tramInteractions',
  'bicycleInteractions', 'vehicleHandling', 'nightVisibility', 'weatherConditions', 'laneChanging', 'overtaking'
] as const;
// KNOWLEDGE: Rules, regulations & vehicle knowledge topics
export const KNOWLEDGE_TOPICS = [
  'speedLimits', 'motorwayRules', 'constructionZones', 'technologySafety', 'fatigueRest',
  'environmentalZones', 'incidentFirstAid', 'vehicleCategories', 'vehicleDocumentation',
  'emergencyProcedures', 'alcoholDrugs', 'specializedDriving', 'adasSafety'
] as const;
export const ARCHIVE_TOPICS = ['realExamQuestions'] as const;

// Topic ID mapping from URL slugs (dashes) to internal IDs (camelCase)
export const TOPIC_SLUG_TO_ID: Record<string, string> = {
  // Tier 1
  'road-markings': 'roadMarkings',
  'motorway-rules': 'motorwayRules',
  'traffic-signs': 'trafficSignRecognition',
  'vehicle-handling': 'vehicleHandling',
  'tram-interactions': 'tramInteractions',
  'risk-awareness': 'riskAwareness',
  'night-visibility': 'nightVisibility',
  'speed-limits': 'speedLimits',
  // Tier 2
  'weather-conditions': 'weatherConditions',
  'incident-first-aid': 'incidentFirstAid',
  'pedestrian-crossings': 'pedestrianCrossings',
  'vehicle-categories': 'vehicleCategories',
  'construction-zones': 'constructionZones',
  'lane-changing': 'laneChanging',
  'overtaking': 'overtaking',
  'technology-safety': 'technologySafety',
  'fatigue-rest': 'fatigueRest',
  'environmental-zones': 'environmentalZones',
  'vehicle-documentation': 'vehicleDocumentation',
  // Tier 3
  'priority-rules': 'priorityRules',
  'mandatory-signs': 'mandatorySigns',
  'traffic-lights-signals': 'trafficLightsSignals',
  'prohibitory-signs': 'prohibitorySigns',
  'roundabout-rules': 'roundaboutRules',
  'emergency-procedures': 'emergencyProcedures',
  'alcohol-drugs': 'alcoholDrugs',
  'specialized-driving': 'specializedDriving',
  // Bonus
  'adas-safety': 'adasSafety'
};

// IMAGE-ONLY TESTS - Backward compatibility
export const IMAGE_ONLY_TESTS = [
  'prohibitory-signs',
  'mandatory-signs',
  'road-signs',
  'sign-identification'
] as const;

export const isImageOnlyTest = (testId: string): boolean => {
  return IMAGE_ONLY_TESTS.includes(testId as any);
};

export const filterImageQuestions = (questions: any[]): any[] => {
  return questions.filter(question => question.imageUrl && question.imageUrl.includes('/signs/'));
};

export const getEnoughImageQuestions = (questions: any[], neededCount: number): any[] => {
  const imageQuestions = filterImageQuestions(questions);
  if (imageQuestions.length >= neededCount) {
    return imageQuestions.slice(0, neededCount);
  }
  return imageQuestions;
};
